/**
 * Editor Sidebar — right panel in edit mode showing publishing, folder, tags, metadata.
 */
import { FileText, FolderOpen, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { DocumentTagsField } from '@/components/library/document-tags-field';
import {
  ItemDepartmentsField,
  type ItemDepartmentsDraft,
} from '@/components/library/item-departments-field';

export interface KBFolderOption {
  id: string;
  name: string;
}

interface EditorSidebarProps {
  status: string;
  /**
   * The article's tags as the editor holds them now. The Tags section is the
   * ONE tag field every document's edit form draws (decision 60, LB6c-T1):
   * it reads the tenant's tag list itself, draws the tag manager beside the
   * chips, says so with a retry when the list cannot be read (never "no
   * tags"), and is drawn only for write:library.documents.
   */
  selectedTagIds: string[];
  folders: KBFolderOption[];
  currentFolderId: string | null;
  onFolderChange: (folderId: string) => void;
  authorName: string;
  createdAt: string;
  version: number;
  onToggleTag: (tagId: string) => void;
  /**
   * The article's departments (decision 31 / B10, LB6-T8) — the same field
   * the document edit form carries; drawn only for a person who holds
   * share:library. Omitted, no departments field.
   */
  departments?: ItemDepartmentsDraft;
}

export interface EditorSidebarBodyProps extends EditorSidebarProps {
  /** When true (Sheet variant), strip the outer fixed-width / border
   *  wrapping so the body fills the Sheet content area. The inline
   *  desktop layout supplies the wrapper itself. */
  embedded?: boolean;
}

/** The pure content of the editor's right metadata panel. Pulled out
 *  so the same body renders both inline (lg+) and inside a responsive
 *  Sheet drawer (< lg, see WikiEditor). */
export function EditorSidebarBody({
  status, selectedTagIds, folders, currentFolderId, onFolderChange,
  authorName, createdAt, version,
  onToggleTag, departments,
  embedded = false,
}: EditorSidebarBodyProps) {
  const wrapperClass = embedded
    ? 'flex flex-col overflow-y-auto'
    : 'w-[300px] bg-muted/10 flex flex-col border-l overflow-y-auto shrink-0';
  return (
    <div className={wrapperClass}>
      <div className="p-4 space-y-6">
        {/* Publishing */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" /> Publishing
          </h4>
          <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
            <span className="text-sm font-medium">Status</span>
            <Badge
              variant={status === 'published' ? 'default' : 'secondary'}
              className={cn(
                'px-3 py-1 text-xs capitalize',
                status === 'published' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-amber-200 text-amber-800 hover:bg-amber-300'
              )}
            >
              {status}
            </Badge>
          </div>
        </div>

        <div className="h-px bg-border" />

        {/* Folder */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <FolderOpen className="h-3.5 w-3.5" /> Folder
          </h4>
          <Select value={currentFolderId ?? ''} onValueChange={onFolderChange}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Select folder" />
            </SelectTrigger>
            <SelectContent>
              {folders.map(f => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="h-px bg-border" />

        {/* Visible to departments — share:library only (LB6-T8) */}
        {departments?.allowed ? (
          <>
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5" /> Visible to departments
              </h4>
              <ItemDepartmentsField draft={departments} showLabel={false} />
            </div>

            <div className="h-px bg-border" />
          </>
        ) : null}

        {/* Tags — the one tag field (decision 60, LB6c-T1); write:library.documents only */}
        <DocumentTagsField frame="sidebar" selectedTagIds={selectedTagIds} onToggle={onToggleTag} />

        <div className="h-px bg-border" />

        {/* Metadata */}
        <div className="space-y-4 text-xs text-muted-foreground">
          <div className="flex justify-between items-center">
            <span>Author</span>
            <span className="font-medium text-foreground bg-muted px-2 py-1 rounded">{authorName}</span>
          </div>
          <div className="flex justify-between items-center">
            <span>Created</span>
            <span>{new Date(createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between items-center">
            <span>Version</span>
            <span className="font-mono">v{version}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Inline desktop variant. Hidden on `< lg` viewports — the WikiEditor
 *  drives a Sheet drawer with the same body content for narrow widths
 *  (#224). */
export function EditorSidebar(props: EditorSidebarProps) {
  return (
    <div className="hidden lg:flex">
      <EditorSidebarBody {...props} />
    </div>
  );
}
